import React, { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { SourceNodeData, HandleConfig } from "../../types"; // Added HandleConfig
import { Position } from "reactflow"; // Added Position
import useStore from "../../store/useStore";
import { Eye, EyeOff } from "lucide-react";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager

interface JavaScriptSourceConfigBoxProps {
  nodeId: string; // Add nodeId prop
  data: SourceNodeData;
  onUpdate?: (data: Partial<SourceNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add optional detach handler prop
  connectedClusterId?: string | null; // Add prop for connected cluster ID
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

const EVENT_VOLUMES = [
  { value: "0-10M", label: "<10M Events" },
  { value: "10-50M", label: "10-50M Events" },
  { value: "50-100M", label: "50-100M Events" },
  { value: "100-500M", label: "100-500M Events" },
  { value: "500M+", label: "500M+ Events" },
];

const EVENT_SPECS = [
  { value: "analytics-js", label: "Analytics.js Standard" },
  { value: "custom", label: "Custom Event Spec" },
  { value: "not-sure", label: "Not sure" },
];

const CONSENT_TOOL_SUGGESTIONS = [
  { value: "OneTrust", label: "OneTrust" },
  { value: "Cookiebot", label: "Cookiebot" },
  { value: "Osano", label: "Osano" },
  { value: "Usercentrics", label: "Usercentrics" },
  { value: "TrustArc", label: "TrustArc" },
  { value: "Quantcast Choice", label: "Quantcast Choice" },
  { value: "Secure Privacy", label: "Secure Privacy" },
  { value: "Didomi", label: "Didomi" },
  { value: "Civic Cookie Control", label: "Civic Cookie Control" },
  { value: "Complianz", label: "Complianz" },
  { value: "Sourcepoint", label: "Sourcepoint" },
  { value: "Iubenda", label: "Iubenda" },
  { value: "ConsentManager.net", label: "ConsentManager.net" },
  { value: "Google Consent Mode", label: "Google Consent Mode" },
  { value: "Adobe Consent Management", label: "Adobe Consent Management" },
  { value: "Piwik PRO Consent Manager", label: "Piwik PRO Consent Manager" },
  { value: "TagCommander CMP", label: "TagCommander CMP" },
  { value: "Matomo Opt-Out", label: "Matomo Opt-Out" },
];

export default function JavaScriptSourceConfigBox({
  nodeId, // Destructure nodeId
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure the detach handler prop
  connectedClusterId, // Destructure the connected cluster ID prop
  portalRoot,
}: JavaScriptSourceConfigBoxProps) {
  // Initialize state with props
  const [label, setLabel] = useState(data.label || "");
  const [url, setUrl] = useState(data.url || "");
  const [eventVolume, setEventVolume] = useState(data.eventVolume || "");
  const [eventSpec, setEventSpec] = useState(data.eventSpec || ""); // Allow empty string for no selection
  const [consentTool, setConsentTool] = useState(data.consentTool || "");
  const [otherConsentToolName, setOtherConsentToolName] = useState(data.otherConsentToolName || "");
  const [notes, setNotes] = useState(data.notes || ""); // Add notes state

  // --- Default Handles for Backward Compatibility ---
  const defaultHandles: HandleConfig[] = [
    { id: 'default-source', type: 'source', position: Position.Right, color: '#3B82F6' }, // Blue
    { id: 'default-target', type: 'target', position: Position.Left, color: '#6366F1' }, // Indigo
  ];
  // --- End Default Handles ---

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() =>
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );
  // --- End Add State for Handles ---

  // NOTE: Removed isOldVersion state. Assume new handle system for this node type.
  // The handles state will be initialized with defaults if data.handles is missing.

  // Initialize visibility settings
  const [urlVisibility, setUrlVisibility] = useState<VisibilityOption>(
    data.urlVisibility === "hidden" ? "hidden" : "visible"
  );
  const [eventVolumeVisibility, setEventVolumeVisibility] = useState<VisibilityOption>(
    data.eventVolumeVisibility === "hidden" ? "hidden" : "visible"
  );
  const [eventSpecVisibility, setEventSpecVisibility] = useState<VisibilityOption>(
    data.eventSpecVisibility !== undefined ? data.eventSpecVisibility : "hidden"
  );
  const [consentToolVisibility, setConsentToolVisibility] = useState<VisibilityOption>(
    data.consentToolVisibility === "hidden" ? "hidden" : "visible"
  );
  const [latencyImpactVisibility, setLatencyImpactVisibility] = useState<VisibilityOption>(
    data.latencyImpactVisibility !== undefined ? data.latencyImpactVisibility : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(data.notesVisibility ?? "visible"); // Add notes visibility state
  
  // Track previous update to prevent loops
  const prevUpdateRef = useRef("");
  // Track if component has initialized to prevent effect firing on mount
  const isInitialized = useRef(false);

  // Flag to prevent updates during state synchronization (might be needed)
  // const isSyncingRef = useRef(false);



  // Get cluster latencies from store (still needed for display)
  const clusterLatencies = useStore((state) => state.clusterLatencies);

  // Debug latency impact (uses connectedClusterId prop now)
  React.useEffect(() => {;
    if (connectedClusterId) {
    }
  }, [connectedClusterId, clusterLatencies, data]);
  
  // Effect to synchronize state with data props
  useEffect(() => {
    // Sync non-handle fields
    setLabel(data.label || "");
    setUrl(data.url || "");
    setEventVolume(data.eventVolume || "");
    setEventSpec(data.eventSpec || ""); // Allow empty string
    setConsentTool(data.consentTool || "");
    setOtherConsentToolName(data.otherConsentToolName || "");
    setNotes(data.notes || "");
    setUrlVisibility(data.urlVisibility === "hidden" ? "hidden" : "visible");
    setEventVolumeVisibility(data.eventVolumeVisibility === "hidden" ? "hidden" : "visible");
    setEventSpecVisibility(data.eventSpecVisibility !== undefined ? data.eventSpecVisibility : "hidden");
    setConsentToolVisibility(data.consentToolVisibility === "hidden" ? "hidden" : "visible");
    setLatencyImpactVisibility(data.latencyImpactVisibility !== undefined ? data.latencyImpactVisibility : "visible");
    setNotesVisibility(data.notesVisibility ?? "visible");

    // Sync handles state more carefully: only update from props if data.handles
    // is an array AND different from the current local 'handles' state.
    // This prevents overwriting the user's action (e.g., removing all handles)
    // before the update effect sends the change to the parent.
    if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
        setHandles([...data.handles]);
    }
    // Do not reset to defaultHandles here; useState initializer handles the initial default.

    // Mark as initialized after the first sync
    if (!isInitialized.current) {
        isInitialized.current = true;
    }
  }, [data]); // Rerun only when data prop changes

  // Update parent when state changes
  useEffect(() => {
    // Prevent firing on initial mount/effect synchronization
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    const updates = {
      label,
      url,
      eventVolume,
      eventSpec,
      consentTool,
      otherConsentToolName,
      urlVisibility,
      eventVolumeVisibility,
      eventSpecVisibility,
      consentToolVisibility,
      latencyImpactVisibility,
      notes, // Add notes to updates
      notesVisibility, // Add notes visibility to updates
      handles: Array.isArray(handles) ? handles : [], // Ensure handles is treated as array here for safety
    };

    const updatesJson = JSON.stringify(updates);
    if (updatesJson !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
    label, 
    url, 
    eventVolume, 
    eventSpec, 
    consentTool,
    otherConsentToolName,
    urlVisibility,
    eventVolumeVisibility,
    eventSpecVisibility,
    consentToolVisibility,
    latencyImpactVisibility,
    notes, // Add notes dependency
    notesVisibility, // Add notes visibility dependency
    handles, // Add handles dependency to trigger update on change
    onUpdate
  ]);

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    // Allow setting empty handles
    setHandles(newHandles);
  }, []);
  // --- End Handler ---

  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* ConfigBox Toolbar */}
      <ConfigBoxToolbar
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onDetachFromGroup={onDetachFromGroup} // Pass the received prop directly
        isAdvancedMode={true}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Name
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Add a name if needed"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Website URL"
          visibility={urlVisibility}
          onToggleVisibility={() => toggleVisibility(urlVisibility, setUrlVisibility)}
          className="mb-1"
        />
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Estimated monthly event volume"
          visibility={eventVolumeVisibility}
          onToggleVisibility={() => toggleVisibility(eventVolumeVisibility, setEventVolumeVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={eventVolume}
          onChange={setEventVolume}
          options={EVENT_VOLUMES}
          placeholder="Select volume"
          dropdownClassName="absolute z-[100]" // Ensure dropdown appears above other elements
          portalContainer={portalRoot} 
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Event Spec"
          visibility={eventSpecVisibility}
          onToggleVisibility={() => toggleVisibility(eventSpecVisibility, setEventSpecVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={eventSpec}
          onChange={setEventSpec}
          options={EVENT_SPECS}
          placeholder="Select event spec"
          dropdownClassName="absolute z-[100]" // Ensure dropdown appears above other elements
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Consent Management Tool"
          visibility={consentToolVisibility}
          onToggleVisibility={() => toggleVisibility(consentToolVisibility, setConsentToolVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={consentTool}
          onChange={setConsentTool}
          options={CONSENT_TOOL_SUGGESTIONS}
          placeholder="Specify your Consent Tool"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>
      
      {/* Latency Impact Display */}
      <div className="px-3 pt-3 pb-3 bg-yellow-50 dark:bg-yellow-300/5  rounded relative">
        <div className="text-xs font-bold text-yellow-800 dark:text-yellow-300">
          Latency Impact: {typeof data.latencyImpact === 'number' ? 
            (data.latencyImpact > 0 ? `${data.latencyImpact}ms` : "0ms") : 
            (connectedClusterId && typeof clusterLatencies[connectedClusterId] === "number" ? 
              `${clusterLatencies[connectedClusterId]}ms` : 
              "Not calculated")}
        </div>
        <button
          type="button"
          onClick={() => toggleVisibility(latencyImpactVisibility, setLatencyImpactVisibility)}
          className={`absolute top-2 right-1 p-1 rounded transition-colors ${
            latencyImpactVisibility === "hidden" 
              ? "bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:text-red-400 dark:hover:bg-red-900/40" 
              : "text-yellow-900/70 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
          }`}
          title={latencyImpactVisibility === "hidden" ? "Show latency impact" : "Hide latency impact"}
        >
          {latencyImpactVisibility === "hidden" ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Notes Section */}
      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this source (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>

      {/* --- Render Handle Manager Unconditionally --- */}
      <div>
        <HandleManager
          nodeId={nodeId} // Use the nodeId prop
          handles={handles} // Use handles state
          onChange={handleHandlesChange}
          maxHandles={6} // Adjust as needed
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}

